import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Chip,
  CircularProgress, Container, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, InputAdornment, MenuItem,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StarIcon from "@mui/icons-material/Star";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EventIcon from "@mui/icons-material/Event";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useNotifier } from "../../Core/Notifier";
import { API_URL } from "../../config";
import { uploadImage } from "../../Components/UploadFile/upload";
import VendorCredentialsModal from "./VendorCredentialsModal";
import { VendorProfileIntelligencePanel } from "../VendorIntelligence";

const buildImgUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_URL}/../${path}`;
};

const T = {
  navy: "#0d1b2a", gold: "#c9a96e", white: "#fff",
  bg: "#f7f5f1", slate: "#5c6b7e", slateLt: "#9ca8b7", line: "#eeebe6",
  greenBg: "#edf7ee", greenTx: "#2d6a35",
  redTx: "#9b2c2c", redBg: "#fef2f2",
  blueBg: "#eff6ff", blueTx: "#1e40af",
  peach: "#ffebd3", inputBg: "#f8f7f5",
};

const inputSx = {
  "& .MuiInputBase-root": { fontSize: 13, borderRadius: "10px", bgcolor: T.inputBg, border: `1px solid ${T.line}`, "&.Mui-focused": { bgcolor: T.peach, borderColor: T.gold }, transition: "all 0.15s", "& fieldset": { border: "none" } },
  "& .MuiInputLabel-root": { fontSize: 12 }, "& input": { py: "8px" },
};

const PRICING_LABELS: Record<string, string> = { fixed: "Per Event", per_guest: "Per Guest", per_hour: "Per Hour", per_unit: "Per Unit" };
const PRICING_COLORS: Record<string, { bg: string; tx: string }> = {
  fixed: { bg: "#e8f5e9", tx: "#2e7d32" },
  per_guest: { bg: "#e3f2fd", tx: "#1565c0" },
  per_hour: { bg: "#fff3e0", tx: "#e65100" },
  per_unit: { bg: "#f3e5f5", tx: "#7b1fa2" },
};

const hdrs = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` });
const get_ = (p: string) => fetch(`${API_URL}${p}`, { headers: hdrs() }).then(r => r.json());
const post = (p: string, b: any) => fetch(`${API_URL}${p}`, { method: "POST", headers: hdrs(), body: JSON.stringify(b) }).then(r => r.json());
const del_ = (p: string) => fetch(`${API_URL}${p}`, { method: "DELETE", headers: hdrs() }).then(r => r.json());

const fmtCAD = (n: number) => n.toLocaleString("en-CA", { style: "currency", currency: "CAD" });

/* ──────────────────────── Item data for modal ──────────────────────── */
interface SelectedItemPricing {
  item_id: number;
  item_name: string;
  category_name: string;
  vendor_price: string;
  dcc_price: string;
  pricing_type: string;
  unit_label: string;
  min_hours: string;
  image_url: string;
}

/* ──────────────────────────── COMPONENT ──────────────────────────── */
const VendorProfilePage: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState<any>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [serviceCategories, setServiceCategories] = useState<any[]>([]);
  const [credModalOpen, setCredModalOpen] = useState(false);
  const [deleteItemTarget, setDeleteItemTarget] = useState<{ id: number; name: string } | null>(null);

  // Add items modal state
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [serviceTree, setServiceTree] = useState<any[]>([]);
  const [modalSearch, setModalSearch] = useState("");
  const [modalCategoryFilter, setModalCategoryFilter] = useState("all");
  const [selectedItems, setSelectedItems] = useState<Map<number, SelectedItemPricing>>(new Map());
  const [addingItems, setAddingItems] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const res = await get_(`/vendors/manage/${id}`);
    if (res.code === 200 && res.data) {
      setVendor(res.data.vendor);
      setAssignments(res.data.assignments || []);
      setStats(res.data.stats || {});
      setServiceCategories(res.data.service_categories || []);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const fetchServiceTree = async () => {
    const res = await get_("/banquet/service-tree");
    if (res.code === 200) setServiceTree(res.data || []);
  };

  /* ── Build flattened items list from service tree ── */
  const flatItems = useMemo(() => {
    const items: { id: number; name: string; description?: string; default_price?: number; categoryId: number; categoryName: string; serviceName: string; subcategoryName?: string }[] = [];
    (serviceTree || []).forEach((svc: any) => {
      (svc.categories || []).forEach((cat: any) => {
        (cat.items || []).forEach((item: any) => {
          items.push({ id: item.id, name: item.name, description: item.description, default_price: item.default_price, categoryId: cat.id, categoryName: cat.name, serviceName: svc.name });
        });
        (cat.subcategories || []).forEach((sub: any) => {
          (sub.items || []).forEach((item: any) => {
            items.push({ id: item.id, name: item.name, description: item.description, default_price: item.default_price, categoryId: cat.id, categoryName: cat.name, serviceName: svc.name, subcategoryName: sub.name });
          });
        });
      });
    });
    return items;
  }, [serviceTree]);

  /* ── Categories from service tree for filter ── */
  const allCategoriesFlat = useMemo(() => {
    const cats: { id: number; name: string; serviceName: string }[] = [];
    (serviceTree || []).forEach((svc: any) => {
      (svc.categories || []).forEach((cat: any) => {
        cats.push({ id: cat.id, name: cat.name, serviceName: svc.name });
      });
    });
    return cats;
  }, [serviceTree]);

  /* ── Group items by category, apply search and filter ── */
  const groupedItems = useMemo(() => {
    const existingItemIds = new Set((vendor?.item_prices || []).map((ip: any) => ip.item_id));
    let filtered = flatItems.filter(i => !existingItemIds.has(i.id));

    if (modalSearch.trim()) {
      const s = modalSearch.toLowerCase();
      filtered = filtered.filter(i => i.name.toLowerCase().includes(s) || i.categoryName.toLowerCase().includes(s) || (i.subcategoryName || "").toLowerCase().includes(s));
    }
    if (modalCategoryFilter !== "all") {
      filtered = filtered.filter(i => String(i.categoryId) === modalCategoryFilter);
    }

    const groups: Record<string, typeof filtered> = {};
    filtered.forEach(i => {
      const key = `${i.serviceName} / ${i.categoryName}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(i);
    });
    return groups;
  }, [flatItems, modalSearch, modalCategoryFilter, vendor]);

  /* ── Handlers ── */
  const handleAddItemOpen = async () => {
    await fetchServiceTree();
    setModalSearch("");
    setModalCategoryFilter("all");
    setSelectedItems(new Map());
    setAddItemOpen(true);
  };

  const toggleItem = (item: any) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.set(item.id, {
          item_id: item.id,
          item_name: item.name,
          category_name: item.categoryName,
          vendor_price: "",
          dcc_price: "",
          pricing_type: "fixed",
          unit_label: "",
          min_hours: "",
          image_url: "",
        });
      }
      return next;
    });
  };

  const toggleAllInCategory = (categoryItems: any[]) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      const allSelected = categoryItems.every(i => next.has(i.id));
      if (allSelected) {
        categoryItems.forEach(i => next.delete(i.id));
      } else {
        categoryItems.forEach(i => {
          if (!next.has(i.id)) {
            next.set(i.id, {
              item_id: i.id,
              item_name: i.name,
              category_name: i.categoryName,
              vendor_price: "",
              dcc_price: "",
              pricing_type: "fixed",
              unit_label: "",
              min_hours: "",
              image_url: "",
            });
          }
        });
      }
      return next;
    });
  };

  const updateSelectedItem = (itemId: number, field: string, value: string) => {
    setSelectedItems(prev => {
      const next = new Map(prev);
      const existing = next.get(itemId);
      if (existing) {
        next.set(itemId, { ...existing, [field]: value });
      }
      return next;
    });
  };

  const handleAddItems = async () => {
    if (selectedItems.size === 0) { showErrorMessage("Select at least 1 item"); return; }

    // Validate all items
    for (const [, item] of Array.from(selectedItems)) {
      if (!item.vendor_price || Number(item.vendor_price) < 0) { showErrorMessage(`Vendor price required for "${item.item_name}"`); return; }
      if (!item.dcc_price || Number(item.dcc_price) < 0) { showErrorMessage(`DCC price required for "${item.item_name}"`); return; }
      if (!item.pricing_type) { showErrorMessage(`Pricing type required for "${item.item_name}"`); return; }
      if (item.pricing_type === "per_hour" && !item.min_hours) { showErrorMessage(`Min hours required for "${item.item_name}" (per hour pricing)`); return; }
    }

    setAddingItems(true);
    const items = Array.from(selectedItems.values()).map(i => ({
      item_id: i.item_id,
      vendor_price: Number(i.vendor_price),
      dcc_price: Number(i.dcc_price),
      pricing_type: i.pricing_type,
      unit_label: i.unit_label || null,
      min_hours: i.pricing_type === "per_hour" && i.min_hours ? Number(i.min_hours) : null,
      image_url: i.image_url || null,
    }));

    const res = await post(`/vendors/manage/${id}/items`, { items });
    if (res.code === 200 && res.status) {
      showMessage(res.message || "Items added");
      setAddItemOpen(false);
      fetchProfile();
    } else {
      showErrorMessage(res.message || "Failed to add items");
    }
    setAddingItems(false);
  };

  const handleRemoveItem = async () => {
    if (!deleteItemTarget) return;
    const res = await del_(`/vendors/manage/${id}/items/${deleteItemTarget.id}`);
    if (res.code === 200 && res.status) { showMessage("Item removed"); fetchProfile(); }
    else showErrorMessage(res.message);
    setDeleteItemTarget(null);
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress sx={{ color: T.gold }} /></Box>;
  }

  if (!vendor) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: "center" }}>
        <Typography sx={{ fontSize: 16, color: T.slateLt }}>Vendor not found.</Typography>
        <Button onClick={() => navigate("/vendors/list")} sx={{ mt: 2, textTransform: "none" }}>Back to Vendors</Button>
      </Container>
    );
  }

  const hasCredentials = !!vendor.vendor_user_id;

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, sm: 2, md: 3, lg: 4 }, pb: 6 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, pt: 1 }}>
        <IconButton onClick={() => navigate("/vendors/list")} sx={{ color: T.slateLt }}><ArrowBackIcon sx={{ fontSize: 20 }} /></IconButton>
        <Box sx={{ width: 48, height: 48, borderRadius: "14px", bgcolor: `${T.gold}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <StorefrontOutlinedIcon sx={{ fontSize: 24, color: T.gold }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: T.navy }}>{vendor.name}</Typography>
            <Chip label={vendor.status} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: vendor.status === "active" ? T.greenBg : T.redBg, color: vendor.status === "active" ? T.greenTx : T.redTx }} />
          </Box>
          {vendor.company_name && <Typography sx={{ fontSize: 13, color: T.slateLt }}>{vendor.company_name}</Typography>}
        </Box>
        <Button variant="outlined" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />} onClick={() => navigate(`/vendors/edit/${id}`)}
          sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: T.line, color: T.navy, borderRadius: "10px", "&:hover": { borderColor: T.gold } }}>
          Edit
        </Button>
      </Box>

      {/* Services Provided */}
      {serviceCategories.length > 0 && (
        <Paper elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.line}`, p: 2.5, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <CategoryOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Services Provided</Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
            {serviceCategories.map((c: any) => (
              <Chip key={c.id} label={c.name} size="small" sx={{
                height: 26, fontSize: 11, fontWeight: 500, px: 0.5,
                bgcolor: `${T.navy}08`, color: T.navy, border: `1px solid ${T.navy}15`,
              }} />
            ))}
          </Box>
        </Paper>
      )}

      {/* Stats Row */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(4,1fr)" }, gap: 2, mb: 3 }}>
        {[
          { label: "Items Supplied", value: stats.item_count || 0, icon: <InventoryOutlinedIcon />, color: T.gold, bg: `${T.gold}12` },
          { label: "Default Items", value: stats.default_item_count || 0, icon: <StarIcon />, color: T.gold, bg: `${T.gold}12` },
          { label: "Total Assignments", value: stats.total_assignments || 0, icon: <AssignmentOutlinedIcon />, color: T.blueTx, bg: T.blueBg },
          { label: "Total Revenue", value: fmtCAD(stats.total_revenue || 0), icon: <TrendingUpIcon />, color: T.greenTx, bg: T.greenBg },
        ].map(s => (
          <Paper key={s.label} elevation={0} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${T.line}`, bgcolor: T.white }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography sx={{ fontSize: 11, color: T.slateLt, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</Typography>
              <Box sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: s.bg, display: "flex", alignItems: "center", justifyContent: "center", "& .MuiSvgIcon-root": { fontSize: 15, color: s.color } }}>{s.icon}</Box>
            </Box>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: T.navy }}>{s.value}</Typography>
          </Paper>
        ))}
      </Box>

      {/* ── Vendor Intelligence ──────────────────────────────────────────── */}
      {id && <VendorProfileIntelligencePanel vendorId={Number(id)} />}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" }, gap: 2.5, mt: 2.5 }}>
        {/* Left Column */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Contact & Address */}
          <Paper elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.line}`, p: 2.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy, mb: 2 }}>Contact & Address</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              {[
                { icon: <EmailOutlinedIcon />, label: "Email", value: vendor.email },
                { icon: <PhoneOutlinedIcon />, label: "Phone", value: vendor.phone },
                { icon: <PhoneOutlinedIcon />, label: "Alt Phone", value: vendor.alternate_phone },
                { icon: <LanguageOutlinedIcon />, label: "Website", value: vendor.website },
                { icon: <LocationOnOutlinedIcon />, label: "Address", value: [vendor.address_line_1 || vendor.street_address, vendor.address_line_2, [vendor.city, vendor.province, vendor.postal_code].filter(Boolean).join(", "), vendor.country].filter(Boolean).join("\n") },
              ].filter(f => f.value).map(f => (
                <Box key={f.label} sx={{ display: "flex", gap: 1 }}>
                  <Box sx={{ "& .MuiSvgIcon-root": { fontSize: 14, color: T.slateLt, mt: 0.3 } }}>{f.icon}</Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: T.slateLt, fontWeight: 600, textTransform: "uppercase" }}>{f.label}</Typography>
                    <Typography sx={{ fontSize: 12, color: T.navy, whiteSpace: "pre-line" }}>{f.value}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            {vendor.notes && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${T.line}` }}>
                <Typography sx={{ fontSize: 10, color: T.slateLt, fontWeight: 600, textTransform: "uppercase", mb: 0.5 }}>Notes</Typography>
                <Typography sx={{ fontSize: 12, color: T.slate }}>{vendor.notes}</Typography>
              </Box>
            )}
          </Paper>

          {/* Items Supplied */}
          <Paper elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.line}`, overflow: "hidden" }}>
            <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${T.line}` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <InventoryOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Items Supplied</Typography>
                <Chip label={vendor.item_prices?.length || 0} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: `${T.gold}12`, color: T.gold }} />
              </Box>
              <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={handleAddItemOpen}
                sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, color: T.navy, "&:hover": { bgcolor: `${T.gold}08` } }}>
                Add Item
              </Button>
            </Box>

            {vendor.item_prices?.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: T.bg }}>
                      {["Item", "Service / Category", "Vendor Price", "DCC Price", "Pricing Type", ""].map(h => (
                        <TableCell key={h} sx={{ fontSize: 10, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.05em", py: 1, borderBottom: `1px solid ${T.line}` }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendor.item_prices.map((ip: any) => {
                      const pc = PRICING_COLORS[ip.pricing_type] || PRICING_COLORS.fixed;
                      return (
                        <TableRow key={ip.id} sx={{ "&:hover": { bgcolor: "#fef6ee" } }}>
                          <TableCell sx={{ py: 1.2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              {(ip.image_url || ip.item?.image_url) ? (
                                <Box component="img" src={buildImgUrl(ip.image_url || ip.item?.image_url) || ""} alt="" sx={{
                                  width: 36, height: 36, borderRadius: "8px", objectFit: "cover",
                                  border: `1px solid ${T.line}`, flexShrink: 0,
                                }} />
                              ) : (
                                <Box sx={{
                                  width: 36, height: 36, borderRadius: "8px", bgcolor: T.bg,
                                  border: `1px solid ${T.line}`, display: "flex", alignItems: "center",
                                  justifyContent: "center", flexShrink: 0,
                                }}>
                                  <ImageOutlinedIcon sx={{ fontSize: 16, color: T.slateLt }} />
                                </Box>
                              )}
                              <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{ip.item?.name ?? "Unknown"}</Typography>
                                {ip.unit_label && <Typography sx={{ fontSize: 10, color: T.slateLt }}>Unit: {ip.unit_label}</Typography>}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1.2 }}>
                            <Typography sx={{ fontSize: 11, color: T.slate }}>{ip.item?.service?.name ?? "-"}</Typography>
                            <Typography sx={{ fontSize: 10, color: T.slateLt }}>{ip.item?.category?.name ?? "-"}{ip.item?.subcategory?.name ? ` / ${ip.item.subcategory.name}` : ""}</Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600, color: T.slate, py: 1.2 }}>{fmtCAD(Number(ip.vendor_price))}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 700, color: T.greenTx, py: 1.2 }}>{fmtCAD(Number(ip.dcc_price))}</TableCell>
                          <TableCell sx={{ py: 1.2 }}>
                            <Chip label={PRICING_LABELS[ip.pricing_type] || ip.pricing_type} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: pc.bg, color: pc.tx }} />
                            {ip.pricing_type === "per_hour" && ip.min_hours && (
                              <Typography sx={{ fontSize: 9, color: T.slateLt, mt: 0.3 }}>Min: {ip.min_hours}h</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1.2 }}>
                            <Tooltip title="Remove item">
                              <IconButton size="small" onClick={() => setDeleteItemTarget({ id: ip.id, name: ip.item?.name || "this item" })} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
                                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <InventoryOutlinedIcon sx={{ fontSize: 32, color: T.slateLt, opacity: 0.4, mb: 1 }} />
                <Typography sx={{ fontSize: 12, color: T.slateLt }}>No items assigned. Use "Add Item" to assign items to this vendor.</Typography>
              </Box>
            )}
          </Paper>

          {/* Upcoming Assignments */}
          <Paper elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.line}`, overflow: "hidden" }}>
            <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1, borderBottom: `1px solid ${T.line}` }}>
              <AssignmentOutlinedIcon sx={{ fontSize: 16, color: T.blueTx }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Upcoming Assignments</Typography>
              <Chip label={assignments.length} size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: T.blueBg, color: T.blueTx }} />
            </Box>

            {assignments.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {assignments.map((a: any) => (
                  <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.5, borderBottom: `1px solid ${T.line}`, "&:hover": { bgcolor: "#fef6ee" } }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: "8px", bgcolor: T.blueBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <EventIcon sx={{ fontSize: 16, color: T.blueTx }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{a.event_name || "Event"}</Typography>
                      <Typography sx={{ fontSize: 10, color: T.slateLt }}>{a.event_date} &middot; {a.item_name}</Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontSize: 11, color: T.slate }}>Qty: {a.quantity || "-"}</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.greenTx }}>{fmtCAD(a.final_price || 0)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <AssignmentOutlinedIcon sx={{ fontSize: 32, color: T.slateLt, opacity: 0.4, mb: 1 }} />
                <Typography sx={{ fontSize: 12, color: T.slateLt }}>No upcoming assignments.</Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Right Column */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Credentials Section */}
          <Paper elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.line}`, p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <VpnKeyOutlinedIcon sx={{ fontSize: 16, color: T.gold }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy }}>Portal Credentials</Typography>
            </Box>

            {hasCredentials ? (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 14, color: T.greenTx }} />
                  <Typography sx={{ fontSize: 12, color: T.greenTx, fontWeight: 600 }}>Portal access enabled</Typography>
                </Box>
                {vendor.vendor_user && (
                  <Box sx={{ bgcolor: T.bg, borderRadius: "10px", p: 1.5, mb: 2 }}>
                    <Typography sx={{ fontSize: 10, color: T.slateLt, fontWeight: 600, textTransform: "uppercase" }}>Login Email</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{vendor.vendor_user.email}</Typography>
                  </Box>
                )}
                <Button fullWidth variant="outlined" onClick={() => setCredModalOpen(true)}
                  startIcon={<VpnKeyOutlinedIcon sx={{ fontSize: 13 }} />}
                  sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: T.line, color: T.navy, borderRadius: "10px", "&:hover": { borderColor: T.gold } }}>
                  Reset Password
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography sx={{ fontSize: 12, color: T.slateLt, mb: 2 }}>No portal credentials set up yet.</Typography>
                <Button fullWidth variant="contained" onClick={() => setCredModalOpen(true)}
                  startIcon={<VpnKeyOutlinedIcon sx={{ fontSize: 14 }} />}
                  sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", "&:hover": { bgcolor: "#1a3550" } }}>
                  Set Up Credentials
                </Button>
              </Box>
            )}
          </Paper>

          {/* Vendor Details Card */}
          <Paper elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${T.line}`, p: 2.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy, mb: 2 }}>Vendor Details</Typography>
            {[
              { label: "First Name", value: vendor.first_name },
              { label: "Last Name", value: vendor.last_name },
              { label: "Company", value: vendor.company_name },
              { label: "Status", value: vendor.status },
              { label: "Created", value: vendor.created_at ? new Date(vendor.created_at).toLocaleDateString() : "-" },
            ].map(d => (
              <Box key={d.label} sx={{ display: "flex", justifyContent: "space-between", py: 0.8, borderBottom: `1px solid ${T.line}` }}>
                <Typography sx={{ fontSize: 11, color: T.slateLt }}>{d.label}</Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.navy }}>{d.value || "-"}</Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      </Box>

      {/* Credentials Modal */}
      <VendorCredentialsModal
        open={credModalOpen}
        vendorId={vendor.id}
        vendorName={vendor.name}
        vendorEmail={vendor.email || vendor.vendor_user?.email || ""}
        hasExistingCredentials={hasCredentials}
        onClose={() => { setCredModalOpen(false); fetchProfile(); }}
        onCreated={fetchProfile}
      />

      {/* ────────── ADD ITEMS MODAL ────────── */}
      <Dialog open={addItemOpen} onClose={() => setAddItemOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", height: "85vh", display: "flex", flexDirection: "column" } }}>
        {/* Modal header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${T.line}`, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: T.navy }}>Add Items to Vendor</Typography>
            <IconButton size="small" onClick={() => setAddItemOpen(false)} sx={{ color: T.slateLt }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField size="small" placeholder="Search items..." value={modalSearch} onChange={e => setModalSearch(e.target.value)} fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: T.slateLt }} /></InputAdornment> }}
              sx={{ ...inputSx, flex: 1 }} />
            <TextField size="small" select value={modalCategoryFilter} onChange={e => setModalCategoryFilter(e.target.value)} sx={{ ...inputSx, minWidth: 180 }}>
              <MenuItem value="all">All Categories</MenuItem>
              {allCategoriesFlat.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
            </TextField>
          </Box>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2 }}>
          {Object.keys(groupedItems).length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <InventoryOutlinedIcon sx={{ fontSize: 40, color: T.slateLt, opacity: 0.3, mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: T.slateLt }}>No items found. Try a different search or category.</Typography>
            </Box>
          ) : (
            Object.entries(groupedItems).map(([categoryKey, items]) => (
              <Accordion key={categoryKey} defaultExpanded disableGutters elevation={0}
                sx={{ border: `1px solid ${T.line}`, borderRadius: "10px !important", mb: 1.5, overflow: "hidden", "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                  sx={{ bgcolor: T.bg, minHeight: 40, px: 2, "& .MuiAccordionSummary-content": { my: 0.8, alignItems: "center", gap: 1 } }}>
                  <CategoryOutlinedIcon sx={{ fontSize: 14, color: T.gold }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{categoryKey}</Typography>
                  <Chip label={items.length} size="small" sx={{ height: 16, fontSize: 9, ml: 0.5, bgcolor: `${T.gold}15`, color: T.gold }} />
                  <Button size="small" onClick={(e) => { e.stopPropagation(); toggleAllInCategory(items); }}
                    sx={{ ml: "auto", textTransform: "none", fontSize: 10, color: T.gold, minWidth: 0, p: "2px 8px" }}>
                    {items.every(i => selectedItems.has(i.id)) ? "Deselect All" : "Select All"}
                  </Button>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {items.map((item, idx) => {
                    const isSelected = selectedItems.has(item.id);
                    return (
                      <Box key={item.id} onClick={() => toggleItem(item)} sx={{
                        display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.2, cursor: "pointer",
                        borderTop: idx > 0 ? `1px solid ${T.line}` : "none",
                        bgcolor: isSelected ? `${T.gold}06` : "transparent",
                        transition: "background-color 0.15s",
                        "&:hover": { bgcolor: isSelected ? `${T.gold}10` : `${T.navy}03` },
                      }}>
                        <Checkbox checked={isSelected} size="small" sx={{ p: 0, color: T.slateLt, "&.Mui-checked": { color: T.gold } }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.navy }}>{item.name}</Typography>
                          {item.subcategoryName && <Typography sx={{ fontSize: 10, color: T.slateLt }}>{item.subcategoryName}</Typography>}
                        </Box>
                        {item.default_price != null && item.default_price > 0 && (
                          <Typography sx={{ fontSize: 10, color: T.slateLt }}>Default: {fmtCAD(item.default_price)}</Typography>
                        )}
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>

        {/* Sticky bottom panel — selected items pricing */}
        {selectedItems.size > 0 && (
          <Box sx={{ borderTop: `2px solid ${T.gold}`, bgcolor: T.white, flexShrink: 0, maxHeight: "45%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ px: 3, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${T.line}` }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.navy }}>
                Selected Items ({selectedItems.size})
              </Typography>
              <Button size="small" onClick={() => setSelectedItems(new Map())}
                sx={{ textTransform: "none", fontSize: 10, color: T.redTx }}>
                Clear All
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 1.5 }}>
              {Array.from(selectedItems.values()).map((item) => (
                <Box key={item.item_id} sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${T.line}`, "&:last-child": { mb: 0, pb: 0, borderBottom: "none" } }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                      {item.image_url ? (
                        <Box sx={{ position: "relative", width: 44, height: 36, borderRadius: "8px", overflow: "hidden", border: `1px solid ${T.line}`, flexShrink: 0 }}>
                          <Box component="img" src={buildImgUrl(item.image_url) || ""} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); updateSelectedItem(item.item_id, "image_url", ""); }}
                            sx={{ position: "absolute", top: 0, right: 0, bgcolor: "rgba(0,0,0,0.5)", width: 14, height: 14, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}>
                            <CloseIcon sx={{ fontSize: 9, color: "#fff" }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button component="label" size="small" sx={{
                          minWidth: 44, height: 36, borderRadius: "8px", border: `1.5px dashed ${T.line}`,
                          bgcolor: T.inputBg, display: "flex", alignItems: "center", justifyContent: "center", p: 0,
                          "&:hover": { borderColor: T.gold },
                        }}>
                          <CloudUploadOutlinedIcon sx={{ fontSize: 16, color: T.slateLt }} />
                          <input type="file" accept="image/*" hidden onClick={e => e.stopPropagation()} onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file || !file.type.startsWith("image/") || file.size > 2 * 1024 * 1024) return;
                            try { const path = await uploadImage(file); if (path) updateSelectedItem(item.item_id, "image_url", path); } catch {}
                          }} />
                        </Button>
                      )}
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{item.item_name}</Typography>
                        <Typography sx={{ fontSize: 10, color: T.slateLt }}>{item.category_name}</Typography>
                      </Box>
                    </Box>
                    <IconButton size="small" onClick={() => { setSelectedItems(prev => { const n = new Map(prev); n.delete(item.item_id); return n; }); }} sx={{ color: T.slateLt, "&:hover": { color: T.redTx } }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: item.pricing_type === "per_hour" ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr", gap: 1.5 }}>
                    <TextField size="small" label="Vendor Price *" type="number" value={item.vendor_price}
                      onClick={e => e.stopPropagation()}
                      onChange={e => updateSelectedItem(item.item_id, "vendor_price", e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={inputSx} />
                    <TextField size="small" label="DCC Price *" type="number" value={item.dcc_price}
                      onClick={e => e.stopPropagation()}
                      onChange={e => updateSelectedItem(item.item_id, "dcc_price", e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={inputSx} />
                    <TextField size="small" label="Pricing Type *" select value={item.pricing_type}
                      onClick={e => e.stopPropagation()}
                      onChange={e => updateSelectedItem(item.item_id, "pricing_type", e.target.value)} sx={inputSx}>
                      {Object.entries(PRICING_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                    </TextField>
                    {item.pricing_type === "per_hour" && (
                      <TextField size="small" label="Min Hours *" type="number" value={item.min_hours}
                        onClick={e => e.stopPropagation()}
                        onChange={e => updateSelectedItem(item.item_id, "min_hours", e.target.value)} sx={inputSx} />
                    )}
                  </Box>
                  {item.pricing_type === "per_unit" && (
                    <TextField size="small" label="Unit Label (e.g. chairs, lights)" value={item.unit_label} fullWidth
                      onClick={e => e.stopPropagation()}
                      onChange={e => updateSelectedItem(item.item_id, "unit_label", e.target.value)}
                      sx={{ ...inputSx, mt: 1.5 }} />
                  )}
                </Box>
              ))}
            </Box>
            <Box sx={{ px: 3, py: 1.5, borderTop: `1px solid ${T.line}`, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button onClick={() => setAddItemOpen(false)} sx={{ textTransform: "none", color: T.slate, fontSize: 12 }}>Cancel</Button>
              <Button variant="contained" onClick={handleAddItems} disabled={addingItems}
                sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, bgcolor: T.navy, borderRadius: "10px", px: 3, "&:hover": { bgcolor: "#1a3550" } }}>
                {addingItems ? "Adding..." : `Add ${selectedItems.size} Item${selectedItems.size > 1 ? "s" : ""}`}
              </Button>
            </Box>
          </Box>
        )}

        {/* Show cancel/close when nothing selected */}
        {selectedItems.size === 0 && (
          <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${T.line}`, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setAddItemOpen(false)} sx={{ textTransform: "none", color: T.slate, fontSize: 12 }}>Close</Button>
          </Box>
        )}
      </Dialog>

      {/* Delete Item Confirmation */}
      <Dialog open={!!deleteItemTarget} onClose={() => setDeleteItemTarget(null)} maxWidth="xs" PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 15 }}><WarningAmberIcon color="error" /> Remove item?</DialogTitle>
        <DialogContent><DialogContentText sx={{ fontSize: 13 }}>Remove <strong>{deleteItemTarget?.name}</strong> from this vendor?</DialogContentText></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteItemTarget(null)} sx={{ textTransform: "none", color: T.slate }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRemoveItem} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VendorProfilePage;
